import {
  IInstructorApprover,
  IInstitution,
  IError,
  LoadingOverlay,
  useToast,
  TextFieldTypes,
} from '@comptia-sso/core';
import { useFormik } from 'formik';
import { ReactElement, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

// Components.
import { Card, EditableContent, EditableDetail } from 'components';

// Hooks.
import { useUpdateInstructorApprover, useInstitutions } from 'hooks';

// Styles.
import styles from './InstructorApproverDetails.module.scss';

// Types.
import { IAccount } from 'types';

interface IInstructorApproverDetailsProps {
  instructorApprover: IInstructorApprover;
  account?: IAccount;
  mode: string;
  defaultInstitutionId?: string;
}

const validationSchema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  institutionId: yup.string().required('Institution is required'),
});

export const InstructorApproverDetails = (
  props: IInstructorApproverDetailsProps,
): ReactElement => {
  const { instructorApprover, account, mode, defaultInstitutionId } = props;
  const [isEditing, setIsEditing] = useState<boolean>(mode !== 'edit');
  const [popToast] = useToast();
  const [updateInstructorApprover] = useUpdateInstructorApprover(
    instructorApprover?.id,
  );
  const [institutions] = useInstitutions();
  const navigate = useNavigate();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: instructorApprover?.email || '',
      firstName: instructorApprover?.firstName || '',
      lastName: instructorApprover?.lastName || '',
      institutionId:
        instructorApprover?.institutionId || defaultInstitutionId || '',
      isActive: instructorApprover?.isActive ?? true,
    },
    onSubmit: async (values) => {
      if (account?.permissions?.editInstructorApprovers) {
        try {
          await updateInstructorApprover(values);
          setIsEditing(false);
          popToast('Instructor Approver successfully saved.');
          if (mode !== 'edit') {
            navigate(`/consent-approvers/institutions/${values.institutionId}`);
          }
        } catch (error) {
          popToast(
            (error as IError)?.message ||
              'An unexpected error occurred. Please try again later.',
          );
        }
      } else {
        popToast('You do not have permission to perform this action.');
      }
    },
    validateOnChange: false,
    validationSchema,
  });

  const activeInstitutions = institutions.filter((i) => i.isActive);

  return (
    <div className={styles.content}>
      <EditableContent
        canEdit={account?.permissions?.editInstructorApprovers ?? false}
        isEditing={isEditing}
        onCancel={() => {
          formik.resetForm();
          if (mode !== 'edit') {
            const instId =
              defaultInstitutionId || instructorApprover?.institutionId;
            navigate(
              instId
                ? `/consent-approvers/institutions/${instId}`
                : '/consent-approvers',
            );
          }
          setIsEditing(false);
        }}
        onEdit={() => setIsEditing(true)}
        formik={formik}
        title="Instructor Approver"
      >
        <Card className={styles.grid}>
          <EditableDetail
            label="Institution"
            name="institutionId"
            required
            select
            renderValue={(value = '') => {
              const inst = institutions.find((i) => i.id === value);
              return inst?.name || value;
            }}
          >
            <option value="">Select Institution</option>
            {activeInstitutions.map((inst) => (
              <option key={inst.id} value={inst.id}>
                {inst.name}
              </option>
            ))}
          </EditableDetail>
          <EditableDetail
            label="Email"
            name="email"
            required
            type={TextFieldTypes.Email}
          />
          <EditableDetail label="First Name" name="firstName" required />
          <EditableDetail label="Last Name" name="lastName" required />
          <EditableDetail
            label="Active"
            name="isActive"
            renderValue={(value) => (value ? 'Yes' : 'No')}
            select
          >
            <option value="true">Yes</option>
            <option value="false">No</option>
          </EditableDetail>
        </Card>
      </EditableContent>
      <LoadingOverlay
        isOpen={formik.isSubmitting}
        text="Saving Instructor Approver"
      />
    </div>
  );
};
